import get from 'lodash.get';
import set from 'lodash.set';
import merge from 'lodash.merge';
import range from 'lodash.range';
import sum from 'lodash.sum';
import { sumAllProps } from '../../utils/helpers';
import { emissionsEmptyData, fuelEmptyData, } from './calculations.state.samples';
import times from 'lodash.times';
const WEEKDAYS = [
    'sunday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
];
const MONTHS = [
    'january',
    'february',
    'march',
    'april',
    'may',
    'june',
    'july',
    'august',
    'september',
    'october',
    'november',
    'december',
];
const dailyTimes = times(24);
function capitalizeFirstLetter(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
}
const notReadyState = (calculations, inputs) => {
    return {
        calculations: {
            isReady: !!calculations,
        },
        inputs: {
            isReady: !!inputs,
        },
    };
};
const readyState = {
    status: {
        calculations: {
            isReady: true,
        },
        inputs: {
            isReady: true,
        },
    },
};
export const getActions = {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    fuel: async (state, setStatus, inputs) => {
        const { calculations } = state;
        if (!inputs || !calculations) {
            setStatus((oldStatus) => ({
                ...oldStatus,
                ...notReadyState(calculations, inputs),
                fuel: { isReady: true },
                rateEngine: { isReady: false },
            }));
            return {};
        }
        const rate = calculations.getDefaultRate(inputs?.assumptionsData.electricityPrice);
        if (!rate) {
            return {};
        }
        const newState = {
            total: {
                annual: {
                    fuel: fuelEmptyData(),
                },
                cumulative: [],
                yearly: [],
            },
            perSet: {},
        };
        const status = {
            ...readyState.status,
            fuel: {
                isReady: true,
            },
            rateEngine: {
                isReady: false,
            },
        };
        const getFuelData = (fuel, fuelValue, electricityValue, totalKwh = 0) => {
            const costInfo = {
                fossil: {
                    [fuel]: fuelValue,
                },
                // INCLUDE PHEV SCENARIO
                electric: {
                    electricity: electricityValue,
                    totalKwh: totalKwh,
                    rateInfo: {
                        energyCost: electricityValue,
                    },
                },
            };
            const fossilTotal = sum(Object.keys(costInfo.fossil).map((key) => costInfo.fossil[key]));
            const electricTotal = costInfo?.electric?.electricity || 0;
            merge(costInfo, {
                fossil: {
                    total: fossilTotal,
                },
                electric: {
                    total: electricTotal,
                },
            });
            const savingInfo = {
                total: fossilTotal - electricTotal,
            };
            return {
                cost: costInfo,
                savings: savingInfo,
            };
        };
        const electricityCostResult = await calculations.annualElectricityCostsWithTotalKwh({
            input: inputs.vehicleSetData,
            rate,
            ...inputs.assumptionsData,
        });
        const annualElectricityValue = electricityCostResult[0].total || 0;
        // const annualKwh = electricityCostResult[0].totalKwh || 0;
        inputs.vehicleSetData.reduce(async (total, vehicleSet) => {
            const annualFuelValue = calculations.annualFossilFuelCosts({
                input: vehicleSet,
                ...inputs.assumptionsData,
            });
            let annualElectricityValue = 0;
            let annualKwh = 0;
            if (!inputs.assumptionsData.isExternalRateEngine) {
                const result = await calculations.annualElectricityCostsWithTotalKwh({
                    input: vehicleSet,
                    rate,
                    ...inputs.assumptionsData,
                });
                annualElectricityValue = result[0].total || 0;
                annualKwh = result[0].totalKwh || 0;
            }
            else {
                annualKwh = calculations.totalKwh({
                    input: vehicleSet,
                    ...inputs.assumptionsData
                });
            }
            const annualFuelObj = merge(fuelEmptyData(), getFuelData(vehicleSet.existingVehicle.fuel, annualFuelValue, annualElectricityValue, annualKwh));
            const yearly = range(inputs.assumptionsData.lifespanYears - 1).reduce((acc, idx) => {
                const fuel = get(vehicleSet, 'existingVehicle.fuel', 'gasoline');
                const totalKwh = calculations.totalKwh({
                    input: vehicleSet,
                    year: inputs.assumptionsData.year + idx,
                });
                const fuelObj = {
                    fuel: getFuelData(fuel, get(annualFuelObj, `cost.fossil.${fuel}`) || 0, get(annualFuelObj, 'cost.electric.electricity') || 0, totalKwh || 0),
                };
                return acc.concat(fuelObj);
            }, [
                {
                    fuel: annualFuelObj,
                },
            ]);
            const cumulative = yearly.reduce((acc, el) => {
                const accumulated = sumAllProps(merge({}, el), acc.at(-1) || {});
                acc.push(accumulated);
                return acc;
            }, []);
            const setResult = {
                annual: {
                    fuel: annualFuelObj,
                },
                cumulative,
                yearly,
            };
            merge(newState.perSet, {
                [vehicleSet.id]: setResult,
            });
            merge(newState.total, sumAllProps(newState.total, setResult));
            return total;
        }, Promise.resolve(newState.total));
        const result = await calculations.annualElectricityCosts({
            input: inputs.vehicleSetData,
            rate,
            ...inputs.assumptionsData,
            isExternalRateEngine: true,
        });
        const ratesInfo = result
            .filter((i) => get(i, 'utility.name', '')?.trim().toLowerCase() ===
            inputs.assumptionsData.selectedUtilityName.trim().toLowerCase())
            .map((r) => ({
            rate: r.rate,
            utility: r.utility,
            cost: r.details,
            totalCost: r.total
        }));
        ratesInfo.push({
            rate: {
                name: 'simple',
                longName: 'Simple Rate Plan',
            },
            cost: {
                energyCost: annualElectricityValue,
            },
        });
        set(newState, 'total.annual.fuel.electricRatesInfo', ratesInfo);
        set(newState, 'status.rateEngine.isReady', true);
        setStatus((s) => ({
            ...s,
            ...status,
            rateEngine: {
                isReady: true,
            },
        }));
        if (inputs.assumptionsData.isExternalRateEngine) {
            const selectedRate = ratesInfo.find((i) => i.rate?.name?.toLowerCase() ===
                inputs.assumptionsData.selectedRateId.toLowerCase()) || {
                rate: result[0].rate,
                utility: result[0].utility,
                cost: result[0].details,
                totalCost: result[0].total
            };
            set(newState, 'total.annual.fuel.cost.electric.electricity', selectedRate?.totalCost);
            set(newState, 'total.annual.fuel.cost.electric.total', selectedRate?.totalCost);
            set(newState, 'total.annual.fuel.cost.electric.totalKwh', 0);
            set(newState, 'total.annual.fuel.cost.electric.rateInfo', selectedRate?.cost);
            newState?.total?.yearly?.forEach((y) => {
                set(y, 'fuel.cost.electric.electricity', selectedRate?.totalCost);
                set(y, 'fuel.cost.electric.total', selectedRate?.totalCost);
                set(y, 'fuel.cost.electric.rateInfo', selectedRate?.cost);
                set(y, 'fuel.savings.total', sum([y.fuel.cost.fossil.total, -y.fuel.cost.electric.total]));
            });
            const cumulative = newState?.total?.yearly?.reduce((acc, el) => {
                const accumulated = sumAllProps(el, acc.at(-1) || {});
                acc.push(accumulated);
                return acc;
            }, []);
            set(newState, 'total.cumulative', cumulative);
            set(newState, 'total.annual.fuel.savings.total', sum([
                get(newState, 'total.annual.fuel.cost.fossil.total'),
                -(selectedRate?.totalCost || 0),
            ]));
            Object.keys(newState?.perSet)?.forEach(setId => {
                newState?.perSet?.[setId]?.yearly?.forEach((y, idx) => {
                    const totalKwh = get(newState, `total.yearly[${idx}].fuel.cost.electric.totalKwh`, 0);
                    const totalKwhPerSet = get(y, 'fuel.cost.electric.totalKwh', 0);
                    const totalPerSet = selectedRate?.totalCost * totalKwhPerSet / totalKwh;
                    set(y, 'fuel.cost.electric.electricity', totalPerSet);
                    set(y, 'fuel.cost.electric.total', totalPerSet);
                    set(y, 'fuel.savings.total', sum([y.fuel.cost.fossil.total, -y.fuel.cost.electric.total]));
                });
                const cumulative = newState?.perSet?.[setId]?.yearly?.reduce((acc, el) => {
                    const accumulated = sumAllProps(el, acc.at(-1) || {});
                    acc.push(accumulated);
                    return acc;
                }, []);
                set(newState, `perSet.${setId}.cumulative`, cumulative);
            });
            return newState;
        }
        else {
            return newState;
        }
    },
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    incentives: (state, setStatus, inputs) => {
        const { calculations } = state;
        if (!inputs || !calculations) {
            setStatus((oldStatus) => ({
                ...oldStatus,
                ...notReadyState(calculations, inputs),
                incentives: { isReady: true },
            }));
            return {};
        }
        const newState = {
            total: {
                cumulative: [],
                yearly: [],
            },
            perSet: {},
        };
        const status = {
            ...readyState.status,
            incentives: {
                isReady: true,
            },
        };
        newState.total = inputs.vehicleSetData.reduce((total, vehicleSet) => {
            const yearly = range(inputs.assumptionsData.lifespanYears).reduce((acc, i) => {
                const incentive = {};
                const lcfs = calculations.lcfsCost({
                    year: get(inputs, 'assumptionsData.year', 0) + i,
                    carbonCreditsPrice: get(inputs, 'assumptionsData.carbonCreditsPrice', 0),
                    input: vehicleSet,
                    state: inputs.assumptionsData.state.toLowerCase(),
                    ...inputs.constants,
                });
                if (acc.length === 0) {
                    /* const incentivesAmount = sum(
                      (get(inputs, 'assumptionsData.incentives') || []).map(
                        ({ estimated_amount }: { estimated_amount: number }) =>
                          estimated_amount,
                      ) || [],
                    ); */
                    const incentivesAmount = 0;
                    merge(incentive, {
                        incentives: {
                            lcfs: -lcfs,
                            others: -incentivesAmount,
                            total: -(incentivesAmount + lcfs),
                        },
                    });
                }
                else {
                    merge(incentive, {
                        incentives: {
                            lcfs: -lcfs,
                            others: 0,
                            total: -lcfs,
                        },
                    });
                }
                return acc.concat(incentive);
            }, []);
            const cumulative = yearly.reduce((acc, el) => {
                const accumulated = sumAllProps(el, acc.at(-1) || {});
                acc.push(accumulated);
                return acc;
            }, []);
            const setResult = {
                yearly,
                cumulative,
            };
            merge(newState.perSet, {
                [vehicleSet.id]: setResult,
            });
            return sumAllProps(total, setResult);
        }, newState.total);
        const incentivesAmount = sum((get(inputs, 'assumptionsData.incentives') || []).map(({ estimated_amount }) => estimated_amount) || []);
        set(newState, 'total.yearly.[0].incentives.others', -incentivesAmount);
        set(newState, 'total.yearly.[0].incentives.total', sum([
            get(newState, 'total.yearly.[0].incentives.others'),
            get(newState, 'total.yearly.[0].incentives.lcfs'),
        ]));
        const cumulative = get(newState, 'total.yearly', []).reduce((acc, el) => {
            const accumulated = sumAllProps(el, acc.at(-1) || {});
            acc.push(accumulated);
            return acc;
        }, []);
        set(newState, 'total.cumulative', cumulative);
        setStatus((s) => ({
            ...s,
            ...status,
            rateEngine: {
                isReady: true,
            },
        }));
        return newState;
    },
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    emissions: (state, setStatus, inputs) => {
        const { calculations } = state;
        if (!inputs || !calculations) {
            setStatus((oldStatus) => ({
                ...oldStatus,
                ...notReadyState(calculations, inputs),
                emissions: { isReady: true },
            }));
            return {};
        }
        const newState = {
            total: {
                annual: {
                    emissions: emissionsEmptyData(),
                },
                cumulative: [],
                yearly: [],
            },
            perSet: {},
        };
        const status = {
            ...readyState.status,
            emissions: {
                isReady: true,
            },
        };
        newState.total = inputs.vehicleSetData.reduce((total, vehicleSet) => {
            const emissionsData = calculations.emissionsData({
                input: vehicleSet,
                ...inputs.assumptionsData,
                ...inputs.constants,
            });
            const annualMiles = calculations.annualMiles({
                input: vehicleSet,
            });
            const annualHours = calculations.annualMiles({
                input: vehicleSet,
            });
            const fuel = vehicleSet.existingVehicle.fuel;
            const yearly = range(inputs.assumptionsData.lifespanYears - 1).reduce((acc) => {
                const emissions = {
                    emissions: {
                        fossil: {
                            [fuel]: {
                                emissions: get(emissionsData, `total${capitalizeFirstLetter(fuel)}Emissions`, 0),
                                volume: get(emissionsData, 'fossilFuelVolume', 0),
                                annualMiles,
                                annualHours,
                            },
                            totalEmissions: get(emissionsData, `total${capitalizeFirstLetter(fuel)}Emissions`, 0),
                            totalVolume: get(emissionsData, 'fossilFuelVolume', 0),
                        },
                        electricity: {
                            emissions: get(emissionsData, 'electricityEmissionsFromEVs'),
                            inLbsPerMwh: get(emissionsData, 'electricityEmissionsInLbsPerMwh'),
                            totalMwh: get(emissionsData, 'totalMwh'),
                        },
                        emissionsReduced: get(emissionsData, 'annualEmissionsReduced'),
                        treesPlanted: get(emissionsData, 'annualTreesPlanted'),
                    },
                };
                return acc.concat(emissions);
            }, [
                {
                    emissions: {
                        fossil: {
                            [fuel]: {
                                emissions: get(emissionsData, `total${capitalizeFirstLetter(fuel)}Emissions`, 0),
                                volume: get(emissionsData, 'fossilFuelVolume', 0),
                                annualMiles,
                                annualHours,
                            },
                            totalEmissions: get(emissionsData, `total${capitalizeFirstLetter(fuel)}Emissions`, 0),
                        },
                        electricity: {
                            emissions: get(emissionsData, 'electricityEmissionsFromEVs'),
                            inLbsPerMwh: get(emissionsData, 'electricityEmissionsInLbsPerMwh'),
                            totalMwh: get(emissionsData, 'totalMwh'),
                        },
                        emissionsReduced: get(emissionsData, 'annualEmissionsReduced'),
                        treesPlanted: get(emissionsData, 'annualTreesPlanted'),
                    },
                },
            ]);
            const cumulative = yearly.reduce((acc, el) => {
                const accumulated = sumAllProps(el, acc.at(-1) || {});
                acc.push(accumulated);
                return acc;
            }, []);
            const setResult = {
                yearly,
                cumulative,
            };
            merge(newState.perSet, {
                [vehicleSet.id]: setResult,
            });
            return sumAllProps(total, setResult);
        }, newState.total);
        setStatus((s) => ({
            ...s,
            ...status,
            rateEngine: {
                isReady: true,
            },
        }));
        return newState;
    },
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    chargers: (state, setStatus, inputs) => {
        const { calculations } = state;
        if (!inputs || !calculations) {
            setStatus((oldStatus) => ({
                ...oldStatus,
                ...notReadyState(calculations, inputs),
                chargers: { isReady: true },
            }));
            return {};
        }
        const newState = {
            total: {
                cumulative: [],
                yearly: [],
            },
            perSet: {},
        };
        const status = {
            ...readyState.status,
            chargers: {
                isReady: true,
            },
        };
        newState.total = inputs.vehicleSetData.reduce((total, vehicleSet) => {
            const chargersPurchaseCost = calculations.chargerPurchaseCosts({
                input: vehicleSet,
            });
            const chargersInstallationCost = inputs.assumptionsData
                .chargerInstallationCost
                ? 0
                : calculations.getInstallationCosts({
                    input: vehicleSet,
                    ...inputs.assumptionsData,
                });
            const yearly = range(inputs.assumptionsData.lifespanYears - 1).reduce((acc, i) => {
                const chargers = {};
                merge(chargers, {
                    chargers: {
                        purchase: 0,
                        installation: 0,
                        total: 0,
                    },
                });
                return acc.concat(chargers);
            }, [
                {
                    chargers: {
                        purchase: chargersPurchaseCost,
                        installation: chargersInstallationCost,
                        total: chargersInstallationCost + chargersPurchaseCost,
                    },
                },
            ]);
            const cumulative = yearly.reduce((acc, el) => {
                const accumulated = sumAllProps(el, acc.at(-1) || {});
                acc.push(accumulated);
                return acc;
            }, []);
            const setResult = {
                yearly,
                cumulative,
            };
            merge(newState.perSet, {
                [vehicleSet.id]: setResult,
            });
            return sumAllProps(total, setResult);
        }, newState.total);
        if (inputs.assumptionsData.chargerInstallationCost) {
            set(newState, 'total.annual.chargers.installation', inputs.assumptionsData.chargerInstallationCost);
            set(newState, 'total.annual.chargers.total', sum([
                get(newState, 'total.annual.chargers.installation'),
                get(newState, 'total.annual.chargers.purchase'),
            ]));
            set(newState, 'total.yearly.[0].chargers.installation', inputs.assumptionsData.chargerInstallationCost);
            set(newState, 'total.yearly.[0].chargers.total', sum([
                get(newState, 'total.yearly.[0].chargers.installation'),
                get(newState, 'total.yearly.[0].chargers.purchase'),
            ]));
            const cumulative = newState.total.yearly.reduce((acc, el) => {
                const accumulated = sumAllProps(el, acc.at(-1) || {});
                acc.push(accumulated);
                return acc;
            }, []);
            set(newState, 'total.cumulative', cumulative);
        }
        setStatus((s) => ({
            ...s,
            ...status,
            rateEngine: {
                isReady: true,
            },
        }));
        return newState;
    },
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    operations: (state, setStatus, inputs) => {
        const { calculations } = state;
        if (!inputs || !calculations) {
            setStatus((oldStatus) => ({
                ...oldStatus,
                ...notReadyState(calculations, inputs),
                operations: { isReady: true },
            }));
            return {};
        }
        const newState = {
            total: {
                cumulative: [],
                yearly: [],
            },
            perSet: {},
        };
        const status = {
            ...readyState.status,
            operations: {
                isReady: true,
            },
        };
        newState.total = inputs.vehicleSetData.reduce((total, vehicleSet) => {
            const yearly = range(inputs.assumptionsData.lifespanYears).reduce((acc, i) => {
                const operations = {};
                const fossilMaintenanceCost = calculations.annualMaintenanceCost({
                    input: vehicleSet,
                    isRetiringFleet: true,
                    ageInYears: i + 1,
                });
                const electricMaintenanceCost = calculations.annualMaintenanceCost({
                    input: vehicleSet,
                    ageInYears: i + 1,
                });
                const fossilInsuranceCost = calculations.annualInsuranceCosts({
                    input: vehicleSet.existingVehicle,
                    count: vehicleSet.vehicleCount,
                    state: inputs.assumptionsData.state,
                    yearsOfOperation: i + 1,
                });
                const electricInsuranceCost = calculations.annualInsuranceCosts({
                    input: vehicleSet.replacementVehicle,
                    count: vehicleSet.vehicleCount,
                    state: inputs.assumptionsData.state,
                    yearsOfOperation: i + 1,
                });
                merge(operations, {
                    operations: {
                        cost: {
                            fossil: {
                                maintenance: fossilMaintenanceCost,
                                insurance: fossilInsuranceCost,
                                total: sum([fossilMaintenanceCost, fossilInsuranceCost]),
                            },
                            electric: {
                                maintenance: electricMaintenanceCost,
                                insurance: electricInsuranceCost,
                                total: sum([electricMaintenanceCost, electricInsuranceCost]),
                            },
                        },
                    },
                });
                set(operations, 'savings.total', sum([
                    get(operations, 'cost.fossil.total', 0),
                    -(get(operations, 'cost.electric.total') || 0),
                ]));
                return acc.concat(operations);
            }, []);
            const cumulative = yearly.reduce((acc, el) => {
                const accumulated = sumAllProps(el, acc.at(-1) || {});
                acc.push(accumulated);
                return acc;
            }, []);
            const setResult = {
                yearly,
                cumulative,
            };
            merge(newState.perSet, {
                [vehicleSet.id]: setResult,
            });
            return sumAllProps(total, setResult);
        }, newState.total);
        setStatus((s) => ({
            ...s,
            ...status,
            rateEngine: {
                isReady: true,
            },
        }));
        return newState;
    },
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    vehicles: (state, setStatus, inputs) => {
        const { calculations } = state;
        if (!inputs || !calculations) {
            setStatus((oldStatus) => ({
                ...oldStatus,
                ...notReadyState(calculations, inputs),
                vehicles: { isReady: true },
            }));
            return {};
        }
        const newState = {
            total: {
                annual: {
                    vehicles: {
                        miles: 0,
                        businessMiles: 0,
                    },
                },
                cumulative: [],
                yearly: [],
            },
            perSet: {},
        };
        const status = {
            ...readyState.status,
            vehicles: {
                isReady: true,
            },
        };
        newState.total = inputs.vehicleSetData.reduce((total, vehicleSet) => {
            const fossilPurchaseCost = calculations.vehiclePurchaseCosts({
                input: vehicleSet,
                isRetiringFleet: true,
            });
            const electricPurchaseCost = calculations.vehiclePurchaseCosts({
                input: vehicleSet,
            });
            const setAnnualMiles = calculations.annualMiles({
                input: vehicleSet,
            });
            const setAnnualBusinessMiles = calculations.annualMiles({
                input: vehicleSet,
            });
            const setAnnualHours = calculations.annualHours({
                input: vehicleSet,
            });
            const chargeSchedule = calculations.averageWorkdayBatterySchedule({
                input: vehicleSet,
                ...inputs.assumptionsData,
            });
            const yearly = range(inputs.assumptionsData.lifespanYears - 1).reduce((acc, i) => {
                const vehicles = {};
                const fossilResaleValue = calculations.calcResaleValue({
                    input: vehicleSet.existingVehicle,
                    yearsOfOperation: i + 2,
                }) * vehicleSet.vehicleCount;
                const electricResaleValue = calculations.calcResaleValue({
                    input: vehicleSet.replacementVehicle,
                    yearsOfOperation: i + 2,
                }) * vehicleSet.vehicleCount;
                const chargeSchedule = calculations.averageWorkdayBatterySchedule({
                    input: vehicleSet,
                    ...inputs.assumptionsData,
                    year: inputs.assumptionsData.year + 1,
                });
                merge(vehicles, {
                    vehicles: {
                        cost: {
                            fossil: {
                                purchase: 0,
                                resale: -fossilResaleValue,
                            },
                            electric: {
                                purchase: 0,
                                resale: -electricResaleValue,
                            },
                        },
                        savings: {
                            total: fossilPurchaseCost -
                                fossilResaleValue -
                                (electricPurchaseCost - electricResaleValue),
                        },
                        count: 0,
                        miles: setAnnualMiles,
                        businessMiles: setAnnualBusinessMiles,
                        hours: setAnnualHours,
                        dailyChargeSchedule: chargeSchedule,
                    },
                });
                return acc.concat(vehicles);
            }, [
                {
                    vehicles: {
                        cost: {
                            fossil: {
                                purchase: fossilPurchaseCost,
                                resale: 0,
                            },
                            electric: {
                                purchase: electricPurchaseCost,
                                resale: 0,
                            },
                        },
                        savings: {
                            total: electricPurchaseCost - fossilPurchaseCost,
                        },
                        count: vehicleSet.vehicleCount,
                        miles: setAnnualMiles,
                        businessMiles: setAnnualBusinessMiles,
                        hours: setAnnualHours,
                        dailyChargeSchedule: chargeSchedule,
                    },
                },
            ]);
            const cumulative = yearly.reduce((acc, el) => {
                const last = merge({}, acc.at(-1));
                merge(last, {
                    vehicles: {
                        cost: {
                            electric: {
                                resale: 0,
                            },
                            fossil: {
                                resale: 0,
                            },
                        },
                    },
                });
                const accumulated = sumAllProps(el, last || {});
                acc.push(accumulated);
                return acc;
            }, []);
            const setResult = {
                annual: {
                    vehicles: {
                        count: vehicleSet.vehicleCount,
                        miles: setAnnualMiles,
                        businessMiles: setAnnualBusinessMiles,
                        hours: setAnnualHours,
                        dailyChargeSchedule: chargeSchedule,
                    },
                },
                yearly,
                cumulative,
            };
            merge(newState.perSet, {
                [vehicleSet.id]: setResult,
            });
            // set(
            //   newState,
            //   'total.annual.vehicles.count',
            //   sum([
            //     get(newState, 'total.annual.vehicles.count', 0),
            //     vehicleSet.vehicleCount,
            //   ]),
            // );
            return sumAllProps(total, setResult);
        }, newState.total);
        setStatus((s) => ({
            ...s,
            ...status,
            rateEngine: {
                isReady: true,
            },
        }));
        return newState;
    },
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    profile: (state, setStatus, inputs) => {
        const { calculations } = state;
        if (!inputs || !calculations) {
            setStatus((oldStatus) => ({
                ...oldStatus,
                ...notReadyState(calculations, inputs),
                profile: { isReady: true },
            }));
            return {};
        }
        const newState = {
            total: {
                cumulative: [],
                yearly: [],
            },
            perSet: {},
            status: {
                ...readyState.status,
                profile: {
                    isReady: true,
                },
            },
        };
        const status = {
            ...readyState.status,
            profile: {
                isReady: true,
            },
        };
        newState.total = inputs.vehicleSetData.reduce((total, vehicleSet) => {
            const annualProfile = {};
            const loadProfile = calculations.annualLoadProfile({
                input: vehicleSet,
                ...inputs.assumptionsData,
                ...inputs.constants,
                year: get(inputs, 'assumptionsData.year', 0),
            });
            const weekdays = WEEKDAYS.reduce((acc, day, idx) => {
                const weekdayLoadProfile = loadProfile.filterBy({
                    daysOfWeek: [idx],
                });
                const batteryPercentage = calculations.averageWorkdayBatterySchedule({
                    input: vehicleSet,
                    ...inputs.assumptionsData,
                    dayOfTheWeek: idx,
                });
                acc.push({
                    day: day,
                    amount: {
                        average: weekdayLoadProfile.sum() /
                            weekdayLoadProfile.expanded().filter((i) => i.load > 0).length,
                        max: weekdayLoadProfile.max(),
                        sum: weekdayLoadProfile.sum(),
                    },
                    cost: {},
                    dailyProfile: dailyTimes.map((hour) => {
                        const dailyHourLoadProfile = weekdayLoadProfile.filterBy({
                            hourStarts: [hour],
                        });
                        return {
                            average: dailyHourLoadProfile.sum() /
                                dailyHourLoadProfile.expanded().filter((i) => i.load > 0)
                                    .length,
                            max: dailyHourLoadProfile.max(),
                            sum: dailyHourLoadProfile.sum(),
                            battery: batteryPercentage[hour],
                        };
                    }),
                });
                return acc;
            }, []);
            const months = MONTHS.reduce((acc, month, idx) => {
                const monthLoadProfile = loadProfile.filterBy({ months: [idx] });
                acc.push({
                    month: month,
                    amount: {
                        average: monthLoadProfile.sum() /
                            monthLoadProfile.expanded().filter((i) => i.load > 0).length,
                        max: monthLoadProfile.max(),
                        sum: monthLoadProfile.sum(),
                    },
                    cost: {},
                });
                return acc;
            }, []);
            merge(annualProfile, {
                months,
                weekdays,
            });
            const setResult = {
                annual: {
                    profile: annualProfile,
                },
            };
            merge(newState.perSet, {
                [vehicleSet.id]: setResult,
            });
            return sumAllProps(total, setResult);
        }, newState.total);
        setStatus((s) => ({
            ...s,
            ...status,
            rateEngine: {
                isReady: true,
            },
        }));
        return newState;
    },
};
